import { Text } from '@mantine/core';
import styled from 'styled-components';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useDownloadedFileBlob } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileBlob';
import { PlacementEditorLayout } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementEditorLayout';
import type { FixedPlacement } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/PlacementCanvas.types';
import type { SlotListItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/SlotList/SlotList.types';
import type { CounterpartyPlacementResult } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/useCounterpartyPlacement.types';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

interface Props {
	dealId: string;
	fileId: string;
	showStampMetadata: boolean;
	/** Every other party's box on this document, drawn read-only so the new ones avoid them. */
	fixedPlacements: FixedPlacement[];
	placement: CounterpartyPlacementResult;
	footer: React.ReactNode;
}

/**
 * Step two of the add-counterparty modal: the live document, with one draggable box per slot
 * the new party will get.
 *
 * `showMetadata` is read-only here — it belongs to the template and every other signer on this
 * document is already stamped against it, so a manager adding a party must not change it.
 */
export const CounterpartyPlacementStep = ({
	dealId,
	fileId,
	showStampMetadata,
	fixedPlacements,
	placement,
	footer,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const fileQuery = useDownloadedFileBlob({ dealId, fileId });

	const slotListItems: SlotListItem[] = placement.slots.map((slot) => ({
		key: slot.key,
		label: slot.label,
		isPlaced: placement.placementsByKey.has(slot.key),
	}));

	if (fileQuery.isLoading) {
		return (
			<LoaderHost>
				<InlineLoader />
			</LoaderHost>
		);
	}

	if (fileQuery.isError || !fileQuery.data) {
		return (
			<Text size="sm" c="red">
				{tMipManage('documents_page.add_counterparty.placement.load_failed')}
			</Text>
		);
	}

	return (
		<EditorHost>
			<PlacementEditorLayout
				file={fileQuery.data.blob}
				showMetadata={showStampMetadata}
				onShowMetadataChange={noop}
				placements={placement.placementsByKey}
				fixedPlacements={fixedPlacements}
				slotByKey={placement.slotByKey}
				selectedSlotKey={placement.selectedSlotKey}
				onSelect={placement.setSelectedSlotKey}
				onPlacementChange={placement.onPlacementChange}
				onRemove={placement.onRemove}
				onPlaceAtDefault={placement.onPlaceAtDefault}
				slotListItems={slotListItems}
				placedCount={placement.placementsByKey.size}
				totalCount={placement.slots.length}
				isComplete={placement.isComplete}
				footer={footer}
			/>
		</EditorHost>
	);
};

const noop = () => undefined;

const LoaderHost = styled.div`
	position: relative;
	min-height: 240px;
`;

// PlacementEditorLayout sizes itself to 100% height, so it needs a parent with a definite one.
const EditorHost = styled.div`
	height: 70vh;
	min-height: 420px;
`;
